/*
 * This file is part of glowingblue/orell-rezensionen.
 *
 * Copyright (c) Glowing Blue AG.
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

import app from 'flarum/forum/app';
import Component, { type ComponentAttrs } from 'flarum/common/Component';
import Tooltip from 'flarum/common/components/Tooltip';
import icon from 'flarum/common/helpers/icon';
import classList from 'flarum/common/utils/classList';
import Stream from 'flarum/common/utils/Stream';
import type Mithril from 'mithril';
import { MAX_RATING } from '../config';

export interface ArticleRatingAttrs extends ComponentAttrs {
	/** The rating to render. Parsed with `Number.parseFloat`, so string sources work too. */
	value: number | string;
	/** When set, the hearts become an input and this receives the picked rating. */
	onChange?: (rating: number) => void;
	/**
	 * When set, a delete affordance is rendered. `false` is part of the type so callers can inline
	 * their permission check (`onDelete={canDelete && handler}`).
	 */
	onDelete?: (() => void) | false;
}

export default class ArticleRating extends Component<ArticleRatingAttrs> {
	/** The rating currently hovered, or `null` while the cursor is elsewhere. */
	hovering: Stream<number | null>;

	oninit(vnode: Mithril.Vnode<ArticleRatingAttrs, this>) {
		super.oninit(vnode);

		this.hovering = Stream<number | null>(null);
	}

	view() {
		const items = Array.from(Array(MAX_RATING).keys()).map((i) => i + 1);
		const isInput = !!this.attrs.onChange;
		const canDelete = !!this.attrs.onDelete;
		const value = Number.parseFloat(this.attrs.value as string);

		return (
			<Tooltip
				text={
					value && !isInput
						? `${app.translator.trans('glowingblue-orell-rezensionen.forum.article_rating.rating_label')} ${value % 1 === 0 ? value.toFixed(0) : value.toFixed(1)}`
						: ''
				}
			>
				<div className={`ArticleRating ${isInput ? 'is-input' : ''}`}>
					{isInput ? <label>{app.translator.trans('glowingblue-orell-rezensionen.forum.composer_review.rating_label')}</label> : null}
					{items.map((item) => {
						let selected = false;
						if (item <= value) {
							selected = true;
						}
						let hovering = this.hovering() && item <= this.hovering()!;

						// For touch devices, turn off hovering, else iOS has an annoying double tap bug
						if ('ontouchstart' in window) {
							hovering = false;
						}

						const className = classList({
							'article-rating-item': true,
							selected,
							hovering,
						});

						// Case where this item is only partially selected
						if (!isInput && !selected && item <= value - (value % 1) + 1) {
							const percent = (value % 1) * 100;
							return (
								<span className={`article-rating-item partial`}>
									{icon('force-fa-style far fa-heart')}
									{icon('force-fa-style fas fa-heart', {
										style: {
											background: `linear-gradient(to right, var(--primary-color) 0%, var(--primary-color) ${percent}%, transparent ${percent}%)`,
											WebkitBackgroundClip: `text`,
											WebkitTextFillColor: `transparent`,
										},
									})}
								</span>
							);
						}

						const attrs: Mithril.Attributes = { className };

						if (isInput) {
							const clickHandler = () => {
								this.attrs.onChange!(item);
							};

							attrs.onclick = clickHandler;
							attrs.ontouchstart = clickHandler; // Needed for iOS
							attrs.onmouseover = () => this.hovering(item);
							attrs.onmouseout = () => this.hovering(null);
						}

						return <span {...attrs}>{icon(selected ? 'force-fa-style fas fa-heart' : 'force-fa-style far fa-heart')}</span>;
					})}

					{canDelete ? (
						// `canDelete` cannot narrow `onDelete` for TypeScript, hence the casts.
						<span
							className="ArticleRating-delete"
							onclick={() => (this.attrs.onDelete as () => void)()}
							ontouchstart={() => (this.attrs.onDelete as () => void)()}
						>
							{icon('far fa-trash')}
						</span>
					) : null}
				</div>
			</Tooltip>
		);
	}
}
